import { getRoot, types } from 'mobx-state-tree';
import { RootType } from '../../rootInstance';
import { HttpVerbs } from '../../constants/verbs';

export interface QuizApiItem {
  id: number;
  name: string;
  quizType: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  createdAt: string;
}

export interface QuizQuestionApiItem {
  id: number;
  quizId: number;
  questionText: string;
  orderNumber: number;
  isActive: boolean;
  image?: string | null;
  createdAt: string;
}

// Quiz question images are written by the BE to its quiz upload destination.
// We store the destination-relative path in the `image` column so both the
// admin panel and the client app can resolve it under IMAGE_BASE_URL.
export const QUIZ_IMAGE_DIR = 'quiz-uploads/';

export const quizImageStoredPath = (filename: string): string =>
  `${QUIZ_IMAGE_DIR}${filename}`;

export const quizImageUrl = (image?: string | null): string =>
  image ? `${process.env.REACT_APP_IMAGE_BASE_URL ?? ''}${image}` : '';

export interface QuizAnswerApiItem {
  id: number;
  questionId: number;
  answerText: string;
  isCorrect: boolean;
  orderNumber: number;
  createdAt: string;
}

export const QuizApi = types
  .model('QuizApi', {})
  .views((self) => {
    return {
      get root(): RootType {
        return getRoot(self);
      },
    };
  })
  .actions((self) => {
    return {
      async getAll(): Promise<QuizApiItem[]> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.GET,
          path: '/quiz',
        });
        return response?.data ?? [];
      },
      async getQuestions(quizId: number): Promise<QuizQuestionApiItem[]> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.GET,
          path: `/quiz/${quizId}/questions`,
        });
        return response?.data ?? [];
      },
      async getAnswers(
        quizId: number,
        questionId: number
      ): Promise<QuizAnswerApiItem[]> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.GET,
          path: `/quiz/${quizId}/questions/${questionId}/answers`,
        });
        return response?.data ?? [];
      },
      async createQuestion(
        quizId: number,
        data: {
          questionText: string;
          orderNumber: number;
          isActive: boolean;
          image?: string | null;
        }
      ): Promise<QuizQuestionApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.POST,
          path: `/quiz/${quizId}/questions`,
          data,
        });
        return response?.data;
      },
      async uploadQuestionImage(quizId: number, file: File): Promise<string> {
        const response = await self.root.axios.uploadFile({
          path: `/quiz/${quizId}/questions/upload`,
          fileKey: 'image',
          file,
        });
        return response?.data?.filename;
      },
      async getQuestion(
        quizId: number,
        questionId: number
      ): Promise<QuizQuestionApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.GET,
          path: `/quiz/${quizId}/questions/${questionId}`,
        });
        return response?.data;
      },
      async updateQuestion(
        quizId: number,
        questionId: number,
        data: Partial<{
          questionText: string;
          orderNumber: number;
          isActive: boolean;
          image: string | null;
        }>
      ): Promise<QuizQuestionApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.PUT,
          path: `/quiz/${quizId}/questions/${questionId}`,
          data,
        });
        return response?.data;
      },
      async getAnswer(
        quizId: number,
        questionId: number,
        answerId: number
      ): Promise<QuizAnswerApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.GET,
          path: `/quiz/${quizId}/questions/${questionId}/answers/${answerId}`,
        });
        return response?.data;
      },
      async updateAnswer(
        quizId: number,
        questionId: number,
        answerId: number,
        data: Partial<{
          answerText: string;
          isCorrect: boolean;
          orderNumber: number;
        }>
      ): Promise<QuizAnswerApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.PUT,
          path: `/quiz/${quizId}/questions/${questionId}/answers/${answerId}`,
          data,
        });
        return response?.data;
      },
      async deleteQuestion(quizId: number, questionId: number): Promise<void> {
        await self.root.axios.requestData({
          type: HttpVerbs.DELETE,
          path: `/quiz/${quizId}/questions/${questionId}`,
        });
      },
      async deleteAnswer(
        quizId: number,
        questionId: number,
        answerId: number
      ): Promise<void> {
        await self.root.axios.requestData({
          type: HttpVerbs.DELETE,
          path: `/quiz/${quizId}/questions/${questionId}/answers/${answerId}`,
        });
      },
      async createQuiz(data: {
        name: string;
        description?: string;
        quizType: string;
        isActive?: boolean;
        startDate: string;
        endDate: string;
        resultsVisibleUntil?: string;
        startTime: string;
        endTime: string;
        dailyWinnersCount?: number;
      }): Promise<QuizApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.POST,
          path: '/quiz',
          data,
        });
        return response?.data;
      },
      async getQuiz(quizId: number): Promise<QuizApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.GET,
          path: `/quiz/${quizId}`,
        });
        return response?.data;
      },
      async updateQuiz(
        quizId: number,
        data: Partial<{
          name: string;
          description: string;
          quizType: string;
          isActive: boolean;
          startDate: string;
          endDate: string;
          resultsVisibleUntil: string;
          startTime: string;
          endTime: string;
          dailyWinnersCount: number;
        }>
      ): Promise<QuizApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.PUT,
          path: `/quiz/${quizId}`,
          data,
        });
        return response?.data;
      },
      async deleteQuiz(quizId: number): Promise<void> {
        await self.root.axios.requestData({
          type: HttpVerbs.DELETE,
          path: `/quiz/${quizId}`,
        });
      },
      async createAnswer(
        quizId: number,
        questionId: number,
        data: { answerText: string; isCorrect: boolean; orderNumber: number }
      ): Promise<QuizAnswerApiItem> {
        const response = await self.root.axios.requestData({
          type: HttpVerbs.POST,
          path: `/quiz/${quizId}/questions/${questionId}/answers`,
          data,
        });
        return response?.data;
      },
    };
  });
